// S9 General: name, description used for routing, data classification, the two personal-details
// answers (REQ-1.4, REQ-10.1; they need no hiding rule, CT-028, and any refusal of them shows under them).
// PATCH /api/apps/:appId with the fields that changed. Also the fallback owner
// (REQ-12.3): an Editor or Admin, the registrant by default; PUT /api/apps/:appId/fallback-owner (poc CT-008:
// moved here from the retired "Roles and groups" section).
import type { AppView, UpdateAppInput } from '@feedback/shared/contracts/apps';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { referenceQuery } from '@/lib/queries';
import { errorText, fieldError, fieldErrors } from '../../lib/admin-api';
import { Button, Field, Input, Notice, Panel, Select, Textarea } from '../../ui/primitives';
import { appPath, useAppWrite } from '../app-data';
import { PersonSearch } from '../pickers';

interface GeneralValues {
  name: string;
  description: string;
  dataClassificationId: string;
  showsGuestPersonalDetails: boolean;
  showsStaffPersonalDetails: boolean;
}

function valuesOf(app: AppView): GeneralValues {
  return {
    name: app.name,
    description: app.description ?? '',
    dataClassificationId: app.dataClassification.id,
    showsGuestPersonalDetails: app.showsGuestPersonalDetails,
    showsStaffPersonalDetails: app.showsStaffPersonalDetails,
  };
}

function changes(before: GeneralValues, after: GeneralValues): UpdateAppInput {
  const body: UpdateAppInput = {};
  if (after.name.trim() !== before.name) body.name = after.name.trim();
  if (after.description.trim() !== before.description) body.description = after.description.trim() || null;
  if (after.dataClassificationId !== before.dataClassificationId)
    body.dataClassificationId = after.dataClassificationId;
  if (after.showsGuestPersonalDetails !== before.showsGuestPersonalDetails)
    body.showsGuestPersonalDetails = after.showsGuestPersonalDetails;
  if (after.showsStaffPersonalDetails !== before.showsStaffPersonalDetails)
    body.showsStaffPersonalDetails = after.showsStaffPersonalDetails;
  return body;
}

export function GeneralSection({ app }: { app: AppView }) {
  return (
    <div className="flex flex-col gap-4">
      <GeneralPanel app={app} />
      <FallbackOwnerPanel app={app} />
    </div>
  );
}

/** Responsible for the tickets the AI can't route to anyone; chosen among the Editors and Admins. */
function FallbackOwnerPanel({ app }: { app: AppView }) {
  const readOnly = !app.can.manageSettings;
  const fallback = useAppWrite(
    app.id,
    (personId: string) => api.put<AppView>(appPath(app.id, '/fallback-owner'), { personId }),
    'Fallback owner saved',
  );
  return (
    <Panel
      title="Fallback owner"
      description="Responsible for tickets the AI can’t route to anyone. An Editor or Admin: whoever registers an app is its first fallback owner."
    >
      <p className="text-sm">
        Now:{' '}
        {app.fallbackOwner ? (
          <strong>{app.fallbackOwner.displayName}</strong>
        ) : (
          <span className="text-muted">nobody, so the Platform admins are responsible</span>
        )}
      </p>
      {readOnly ? null : (
        <div className="mt-3 max-w-md">
          <PersonSearch label="Choose the fallback owner" onPick={(p) => fallback.mutate(p.principalId)} />
        </div>
      )}
      {fallback.isError ? (
        <Notice tone="danger" className="mt-2">
          {errorText(fallback.error)}
        </Notice>
      ) : null}
    </Panel>
  );
}

function GeneralPanel({ app }: { app: AppView }) {
  const reference = useQuery(referenceQuery);
  const [values, setValues] = useState(() => valuesOf(app));
  const saved = valuesOf(app);
  const update = useAppWrite(
    app.id,
    (body: UpdateAppInput) => api.patch<AppView>(appPath(app.id), body),
    'General settings saved',
  );
  const errors = fieldErrors(update.error);
  const personalDetailsError =
    fieldError(errors, 'showsGuestPersonalDetails') ?? fieldError(errors, 'showsStaffPersonalDetails');
  const body = changes(saved, values);
  const readOnly = !app.can.manageSettings;

  useEffect(() => {
    setValues(valuesOf(app));
  }, [app]);

  return (
    <Panel title="General">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(body);
        }}
      >
        <Field label="Name" required error={fieldError(errors, 'name')}>
          {({ id, invalid }) => (
            <Input
              id={id}
              aria-invalid={invalid}
              value={values.name}
              disabled={readOnly}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
            />
          )}
        </Field>
        <Field label="Description, used for routing" hint="What the app does; the AI uses it to route reports.">
          {({ id, describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              value={values.description}
              disabled={readOnly}
              onChange={(e) => setValues({ ...values, description: e.target.value })}
            />
          )}
        </Field>
        <Field label="Data classification" required error={fieldError(errors, 'dataClassificationId')}>
          {({ id }) => (
            <Select
              id={id}
              value={values.dataClassificationId}
              disabled={readOnly}
              onChange={(e) => setValues({ ...values, dataClassificationId: e.target.value })}
            >
              {reference.data?.dataClassifications.some((c) => c.id === values.dataClassificationId) ? null : (
                <option value={app.dataClassification.id}>{app.dataClassification.label}</option>
              )}
              {reference.data?.dataClassifications.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">Personal details the app shows</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={values.showsGuestPersonalDetails}
              disabled={readOnly}
              onChange={(e) => setValues({ ...values, showsGuestPersonalDetails: e.target.checked })}
            />
            Guests
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={values.showsStaffPersonalDetails}
              disabled={readOnly}
              onChange={(e) => setValues({ ...values, showsStaffPersonalDetails: e.target.checked })}
            />
            Staff
          </label>
          {personalDetailsError ? (
            <p className="text-xs font-medium text-danger" role="alert">
              {personalDetailsError}
            </p>
          ) : null}
        </fieldset>
        {update.isError && Object.keys(errors).length === 0 ? (
          <Notice tone="danger">{errorText(update.error)}</Notice>
        ) : null}
        {readOnly ? null : (
          <div>
            <Button variant="primary" type="submit" busy={update.isPending} disabled={Object.keys(body).length === 0}>
              Save
            </Button>
          </div>
        )}
      </form>
    </Panel>
  );
}
